/**
 * @file   src\pages\Settings.js
 * @brief  This file is responsible for handling account settings.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchUserDetails } from "../actions/userprofile";
import { getUserDetails } from "../services/userprofileService";
import {
  NOT_FREE_OR_MEMBER_PERIOD,
  USER_INFO_KEY,
  USER_TYPE_AGENT,
} from "../constants/common";
import { getLocalStorage } from "../helpers/common";
import ProfileNormalUser from "./ProfileNormalUser";
import ProfileAgentUser from "./ProfileAgentUser";
import ProfileAgentUserPremium from "./v2/ProfileAgentUserPremium";
import Notifications from "./Notifications";
import PlansBilling from "@/components/PlansBilling";
import Loader from "../components/Common/Loader";
import { useLocation } from "react-router-dom";
import { PageWrapper } from "@/components/PageWrapper";
import BioStudio from "@/components/BioStudio";
import EditProfileModal from "@/components/MemberWantAd/v2/EditProfileModal";
import { QUIET_PROGRESS_OPTIONS, useOnboardingProgress } from "@/hooks/useOnboardingProgress";
import UnderlineTabs from "@/components/UnderlineTabs";
import BrokerageStudio from "@/components/BrokerageStudio";
import MyBrokerageRow from "@/components/MyBrokerageRow";
import { useManagedBrokerages } from "@/hooks/useBrokerageManage";
import { useAppConfig } from "@/appConfig";
import AgentWebsiteRow from "@/components/AgentWebsiteRow";
import SettingsShell from "@/components/SettingsShell";

const USER_DETAILS_KEY = "userDetails";

const SETTINGS_TABS = ["profile", "brokerage", "plans", "notifications", "account"];
const TAB_ALIASES = {
  bio: "profile",
  showcase: "brokerage",
  office: "brokerage",
  "brokerage-plus": "brokerage",
  payments: "plans",
  billing: "plans",
};
const PLAN_SECTIONS = ["plan", "billing"];

export const requestedSettingsTab = (search) => {
  const params = new URLSearchParams(search || "");
  if (params.get("plan") || params.get("plus")) return "plans";
  if (params.get("showcase")) return params.get("tab") === "profile" ? "profile" : "brokerage";
  const raw = params.get("tab");
  if (raw === "office" && PLAN_SECTIONS.includes(params.get("section"))) return "plans";
  const requested = TAB_ALIASES[raw] || raw;
  return SETTINGS_TABS.includes(requested) ? requested : null;
};

export const initialSettingsTab = (search, addNewCard, editAgentProfile) => {
  if (addNewCard) return "plans";
  if (editAgentProfile) return "profile";
  return requestedSettingsTab(search);
};

const Settings = (props) => {
  const { addNewCard, editAgentProfile } = props;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const sessionData = getLocalStorage(USER_INFO_KEY);
  const userId = _.get(sessionData, "userInfo.userId", "");
  const location = useLocation();
  const isFromNotification =
    location.state && location.state["isFromNotification"];
  const [requestedKey, setKey] = useState(() =>
    initialSettingsTab(location.search, addNewCard, editAgentProfile),
  );
  const [detailsOpen, setDetailsOpen] = useState(Boolean(editAgentProfile));

  useEffect(() => {
    const requested = requestedSettingsTab(location.search);
    if (requested) setKey(requested);
  }, [location.search]);

  const userDetailsQuery = useQuery({
    queryKey: [USER_DETAILS_KEY],
    queryFn: async () => {
      const response = await getUserDetails();
      // Mirror into Redux so the shared Basic/Premium plan modals rendered under
      // this page, which read state.userProfile.userDetails, keep working
      // without issuing a second network request.
      dispatch(fetchUserDetails.fulfilled(response, "rq-mirror"));
      return response;
    },
    enabled: !!userId,
    // Settings is where plan/profile changes land, so always re-read on mount
    // instead of serving the globally configured 5 minute stale window.
    staleTime: 0,
  });

  useEffect(() => {
    if (!editAgentProfile) return;
    setKey("profile");
    setDetailsOpen(true);
    userDetailsQuery.refetch({ cancelRefetch: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAgentProfile]);

  const userPlans = useMemo(
    () => _.get(userDetailsQuery.data, "result", null),
    [userDetailsQuery.data],
  );

  const userDetails = userPlans?.length > 0 ? userPlans[0] : null;
  const upComingPlanDetails =
    userPlans?.length > 1 ? userPlans[userPlans.length - 1] : null;
  const userType = userDetails?.UserTypeId;
  const { flags } = useAppConfig();
  const isAgent =
    userType === USER_TYPE_AGENT ||
    (!userDetails && _.get(sessionData, "userInfo.userType", null) === USER_TYPE_AGENT);
  const isBlsAgent = Boolean(_.get(sessionData, "userInfo.mls", null));
  const roster = useOnboardingProgress(
    Boolean(isAgent && isBlsAgent),
    QUIET_PROGRESS_OPTIONS,
  ).data?.roster;
  const managedBrokerages = useManagedBrokerages(Boolean(isAgent && isBlsAgent)).data || [];
  const canManageBrokerage = managedBrokerages.length > 0;
  const [brokerageId, setBrokerageId] = useState(() => {
    const requested = Number(new URLSearchParams(location.search).get("brokerage"));
    return Number.isInteger(requested) && requested > 0 ? requested : null;
  });
  const selectedBrokerageId =
    managedBrokerages.find((item) => item.id === brokerageId)?.id || managedBrokerages[0]?.id || null;
  const defaultTab = isAgent ? "profile" : "account";
  const key =
    requestedKey === "profile" && !isAgent
      ? "account"
      : requestedKey === "brokerage" && !canManageBrokerage
        ? defaultTab
        : requestedKey || defaultTab;

  const updateReloadParam = () => {
    queryClient.invalidateQueries({ queryKey: [USER_DETAILS_KEY] });
  };

  const renderAccountTab = () => {
    if (userDetailsQuery.isLoading) {
      return <Loader />;
    }

    if (userDetailsQuery.isError) {
      return (
        <div className="text-center pt-4">
          Unable to load your account details. Please refresh the page and try
          again.
        </div>
      );
    }

    if (userType === 1) {
      return (
        <ProfileNormalUser
          userDetails={userDetails}
          updateReloadParam={updateReloadParam}
          upComingPlanDetails={upComingPlanDetails}
          isFromNotification={isFromNotification}
        />
      );
    }

    const plansElsewhere = userType === 2 && isBlsAgent;

    if (userType === 2 && userDetails?.period > NOT_FREE_OR_MEMBER_PERIOD) {
      return (
        <ProfileAgentUserPremium
          userDetails={userDetails}
          updateReloadParam={updateReloadParam}
          upComingPlanDetails={upComingPlanDetails}
          isFromNotification={isFromNotification}
          hidePlanHeader={plansElsewhere}
        />
      );
    }

    return (
      <ProfileAgentUser
        userDetails={userDetails}
        updateReloadParam={updateReloadParam}
        upComingPlanDetails={upComingPlanDetails}
        isFromNotification={isFromNotification}
        hidePlanHeader={plansElsewhere}
      />
    );
  };

  return (
    <>
      <PageWrapper>
        <div className="m-0 pb-4">
          <div className="main-content p-0 agent-no-login-content">
            <div className="row m-0">
              <div className="col-md-12">
                <div
                  className="edit-want-ad-wrap settings-wrap settings-wrap--wide"
                >
                  <SettingsShell>
                    <h3 className="heading mb-20">Settings</h3>

                    <UnderlineTabs
                      label="Settings"
                      tabs={[
                        ...(isAgent ? [{ key: "profile", label: "Profile" }] : []),
                        ...(canManageBrokerage ? [{ key: "brokerage", label: "Brokerage" }] : []),
                        { key: "plans", label: "Plans and billing" },
                        { key: "notifications", label: "Notifications" },
                        { key: "account", label: "Account" },
                      ]}
                      value={key}
                      onChange={setKey}
                    />

                    {key === "account" ? (
                      <div className="settings-pane">{renderAccountTab()}</div>
                    ) : null}
                    {key === "profile" && isAgent ? (
                      userDetails ? (
                        <>
                          {isBlsAgent && !canManageBrokerage ? <MyBrokerageRow /> : null}
                          {isBlsAgent ? <AgentWebsiteRow website={userDetails.websiteUrl || ""} name={[userDetails.FirstName, userDetails.LastName].filter(Boolean).join(" ")} /> : null}
                          <BioStudio
                            userDetails={userDetails}
                            isBlsAgent={isBlsAgent}
                            onEditDetails={() => setDetailsOpen(true)}
                            roster={roster}
                            updateReloadParam={updateReloadParam}
                          />
                        </>
                      ) : userDetailsQuery.isError ? (
                        <div className="text-center pt-4">
                          Your profile did not load.{" "}
                          <button
                            type="button"
                            className="btn btn-link p-0 align-baseline"
                            onClick={() => userDetailsQuery.refetch()}
                          >
                            Try again
                          </button>
                        </div>
                      ) : (
                        <Loader />
                      )
                    ) : null}
                    {key === "brokerage" && canManageBrokerage ? (
                      <BrokerageStudio
                        brokerages={managedBrokerages}
                        selectedId={selectedBrokerageId}
                        onSelect={setBrokerageId}
                        requestedId={brokerageId}
                      />
                    ) : null}
                    {key === "plans" ? (
                      <div className="settings-pane">
                        <PlansBilling
                          addNewCard={addNewCard}
                          isBlsAgent={isAgent && isBlsAgent}
                          brokerages={managedBrokerages}
                          brokerageId={selectedBrokerageId}
                        />
                      </div>
                    ) : null}
                    {key === "notifications" ? (
                      <div className="settings-pane">
                        <Notifications userDetails={userDetails} />
                      </div>
                    ) : null}
                  </SettingsShell>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>
      {isAgent && userDetails ? (
        <EditProfileModal
          userDetails={userDetails}
          show={detailsOpen}
          onHide={() => setDetailsOpen(false)}
          updateReloadParam={updateReloadParam}
          editAgentProfile={editAgentProfile}
          roster={roster}
        />
      ) : null}
    </>
  );
};

export default Settings;
